import { useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { BookOpen, Copy, KeyRound, Pause, Play, Plus, RefreshCw, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { FormField } from '@/components/ui/FormField';
import { Badge } from '@/components/ui/Badge';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { toast } from '@/store/toastStore';
import {
  createKnowledgeBaseSource,
  deleteKnowledgeBaseSource,
  kbQueryKeys,
  listKnowledgeBaseSources,
  regenerateKnowledgeBaseSourceSecret,
  updateKnowledgeBaseSource,
  type KnowledgeBaseSource,
  type KnowledgeBaseSourceWithSecret,
} from '@/services/knowledge-base.service';

const SOURCE_TYPE_OPTIONS = [
  { value: 'delightree_zapier', label: 'Delightree via Zapier' },
  { value: 'zapier', label: 'Zapier' },
  { value: 'manual', label: 'Manual' },
];

function formatDate(value: string | null) {
  if (!value) return 'Never';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Never';
  return date.toLocaleString();
}

function sourceTypeLabel(value: string) {
  return SOURCE_TYPE_OPTIONS.find((option) => option.value === value)?.label ?? value;
}

async function copyText(value: string, label: string) {
  await navigator.clipboard.writeText(value);
  toast.success(`${label} copied`);
}

function SecretPanel({ source }: { source: KnowledgeBaseSourceWithSecret | null }) {
  if (!source) return null;

  return (
    <div className="rounded-lg border border-amber-200 bg-amber-50 p-4">
      <div className="flex items-start gap-3">
        <KeyRound className="mt-0.5 h-5 w-5 text-amber-600" />
        <div className="min-w-0 flex-1">
          <h3 className="text-sm font-semibold text-amber-900">Save this webhook secret now</h3>
          <p className="mt-1 text-sm text-amber-800">
            This secret is only shown after creation or regeneration. Add it to Zapier as the
            `X-KB-Webhook-Secret` header.
          </p>
          <div className="mt-3 grid gap-3">
            <div>
              <div className="mb-1 text-xs font-medium uppercase tracking-wide text-amber-700">Webhook URL</div>
              <div className="flex gap-2">
                <code className="min-w-0 flex-1 break-all rounded border border-amber-200 bg-white px-3 py-2 text-xs text-slate-700">
                  {source.webhookUrl}
                </code>
                <Button
                  variant="secondary"
                  size="sm"
                  leftIcon={<Copy className="h-3.5 w-3.5" />}
                  onClick={() => copyText(source.webhookUrl, 'Webhook URL')}
                >
                  Copy
                </Button>
              </div>
            </div>
            <div>
              <div className="mb-1 text-xs font-medium uppercase tracking-wide text-amber-700">Webhook Secret</div>
              <div className="flex gap-2">
                <code className="min-w-0 flex-1 break-all rounded border border-amber-200 bg-white px-3 py-2 text-xs text-slate-700">
                  {source.webhookSecret}
                </code>
                <Button
                  variant="secondary"
                  size="sm"
                  leftIcon={<Copy className="h-3.5 w-3.5" />}
                  onClick={() => copyText(source.webhookSecret, 'Webhook secret')}
                >
                  Copy
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function statusBadgeVariant(status: string): 'success' | 'warning' | 'neutral' {
  if (status === 'active') return 'success';
  if (status === 'paused') return 'warning';
  return 'neutral';
}

interface SourceCardProps {
  source: KnowledgeBaseSource;
  onRegenerate: (sourceId: number) => void;
  onTogglePause: (source: KnowledgeBaseSource) => void;
  onDelete: (sourceId: number) => void;
  isRegenerating: boolean;
  isUpdating: boolean;
}

function SourceCard({ source, onRegenerate, onTogglePause, onDelete, isRegenerating, isUpdating }: SourceCardProps) {
  const isPaused = source.status === 'paused';
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="text-base font-semibold text-slate-900">{source.name}</h3>
            <Badge variant={statusBadgeVariant(source.status)}>{source.status}</Badge>
          </div>
          <div className="mt-1 text-sm text-slate-500">{sourceTypeLabel(source.sourceType)}</div>
          <div className="mt-3 grid gap-1 text-sm text-slate-600 sm:grid-cols-2">
            <div>
              Last sync: <span className="font-medium text-slate-800">{formatDate(source.lastSyncedAt)}</span>
            </div>
            <div>
              Created: <span className="font-medium text-slate-800">{formatDate(source.createdAt)}</span>
            </div>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          {source.sourceType !== 'manual' && (
            <Button
              variant="secondary"
              size="sm"
              loading={isUpdating}
              leftIcon={isPaused ? <Play className="h-3.5 w-3.5" /> : <Pause className="h-3.5 w-3.5" />}
              onClick={() => onTogglePause(source)}
            >
              {isPaused ? 'Resume Sync' : 'Pause Sync'}
            </Button>
          )}
          <Button
            variant="secondary"
            size="sm"
            loading={isRegenerating}
            leftIcon={<RefreshCw className="h-3.5 w-3.5" />}
            onClick={() => onRegenerate(source.kbSourceId)}
          >
            Regenerate Secret
          </Button>
          <Button
            variant="ghost"
            size="sm"
            aria-label="Delete"
            onClick={() => onDelete(source.kbSourceId)}
          >
            <Trash2 className="h-4 w-4 text-red-500" />
          </Button>
        </div>
      </div>

      {source.webhookUrl && (
        <div className="mt-4">
          <div className="mb-1 text-xs font-medium uppercase tracking-wide text-slate-500">Webhook URL</div>
          <div className="flex gap-2">
            <code className="min-w-0 flex-1 break-all rounded border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-700">
              {source.webhookUrl}
            </code>
            <Button
              variant="secondary"
              size="sm"
              leftIcon={<Copy className="h-3.5 w-3.5" />}
              onClick={() => copyText(source.webhookUrl!, 'Webhook URL')}
            >
              Copy
            </Button>
          </div>
        </div>
      )}

      {isPaused && source.sourceType !== 'manual' && (
        <p className="mt-3 text-xs text-amber-700">
          Sync is paused. Documents from this source can now be edited manually. Resuming sync
          will overwrite any local edits the next time the source pushes a matching document.
        </p>
      )}
    </div>
  );
}

export function SourcesPage() {
  const queryClient = useQueryClient();
  const [name, setName] = useState('Delightree Knowledge Base');
  const [sourceType, setSourceType] = useState('delightree_zapier');
  const [latestSecret, setLatestSecret] = useState<KnowledgeBaseSourceWithSecret | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<number | null>(null);

  const sourcesQuery = useQuery({
    queryKey: kbQueryKeys.sources(),
    queryFn: listKnowledgeBaseSources,
  });

  const createMutation = useMutation({
    mutationFn: createKnowledgeBaseSource,
    onSuccess: (source) => {
      setLatestSecret(source);
      queryClient.invalidateQueries({ queryKey: kbQueryKeys.sources() });
      toast.success('Knowledge base connection created');
    },
    onError: () => toast.error('Failed to create knowledge base connection'),
  });

  const regenerateMutation = useMutation({
    mutationFn: regenerateKnowledgeBaseSourceSecret,
    onSuccess: (source) => {
      setLatestSecret(source);
      queryClient.invalidateQueries({ queryKey: kbQueryKeys.sources() });
      toast.success('Webhook secret regenerated');
    },
    onError: () => toast.error('Failed to regenerate webhook secret'),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, status }: { id: number; status: 'active' | 'paused' }) =>
      updateKnowledgeBaseSource(id, { status }),
    onSuccess: (_, vars) => {
      queryClient.invalidateQueries({ queryKey: kbQueryKeys.sources() });
      toast.success(vars.status === 'paused' ? 'Sync paused' : 'Sync resumed');
    },
    onError: () => toast.error('Failed to update source'),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => deleteKnowledgeBaseSource(id, true),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: kbQueryKeys.all });
      toast.success('Source deleted');
      setConfirmDelete(null);
    },
    onError: () => toast.error('Failed to delete source'),
  });

  function handleCreate(e: FormEvent) {
    e.preventDefault();
    const trimmedName = name.trim();
    if (!trimmedName) {
      toast.error('Connection name is required');
      return;
    }
    createMutation.mutate({ name: trimmedName, sourceType });
  }

  return (
    <div className="space-y-6">
      <SecretPanel source={latestSecret} />

      <div className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_360px]">
        <div className="space-y-4">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <BookOpen className="h-5 w-5 text-slate-500" />
              <h2 className="text-base font-semibold text-slate-900">Connections</h2>
            </div>
            <Button
              variant="secondary"
              size="sm"
              leftIcon={<RefreshCw className="h-3.5 w-3.5" />}
              loading={sourcesQuery.isFetching}
              onClick={() => sourcesQuery.refetch()}
            >
              Refresh
            </Button>
          </div>

          {sourcesQuery.isLoading ? (
            <div className="rounded-lg border border-slate-200 bg-white p-6 text-sm text-slate-500">
              Loading connections...
            </div>
          ) : sourcesQuery.data?.length ? (
            <div className="space-y-3">
              {sourcesQuery.data.map((source) => (
                <SourceCard
                  key={source.kbSourceId}
                  source={source}
                  onRegenerate={(id) => regenerateMutation.mutate(id)}
                  onTogglePause={(s) =>
                    updateMutation.mutate({
                      id: s.kbSourceId,
                      status: s.status === 'paused' ? 'active' : 'paused',
                    })
                  }
                  onDelete={(id) => setConfirmDelete(id)}
                  isRegenerating={regenerateMutation.isPending}
                  isUpdating={updateMutation.isPending}
                />
              ))}
            </div>
          ) : (
            <div className="rounded-lg border border-dashed border-slate-300 bg-white p-8 text-center">
              <BookOpen className="mx-auto h-8 w-8 text-slate-300" />
              <h3 className="mt-3 text-sm font-semibold text-slate-900">No knowledge base connections yet</h3>
              <p className="mt-1 text-sm text-slate-500">
                Create a Zapier connection, then configure the Zap to post KB content to the webhook.
              </p>
            </div>
          )}
        </div>

        <div className="space-y-4">
          <form onSubmit={handleCreate} className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
            <h2 className="text-base font-semibold text-slate-900">Create Zapier Connection</h2>
            <div className="mt-4 space-y-4">
              <Input
                label="Connection Name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Delightree Knowledge Base"
              />
              <FormField label="Source Type">
                <select
                  value={sourceType}
                  onChange={(e) => setSourceType(e.target.value)}
                  className="form-input-base"
                >
                  {SOURCE_TYPE_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </FormField>
              <Button type="submit" fullWidth loading={createMutation.isPending} leftIcon={<Plus className="h-4 w-4" />}>
                Create Connection
              </Button>
            </div>
          </form>

          <div className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
            <h2 className="text-base font-semibold text-slate-900">Zapier Payload</h2>
            <p className="mt-2 text-sm text-slate-600">
              Configure Zapier to POST JSON to the webhook URL with the secret in
              `X-KB-Webhook-Secret`.
            </p>
            <pre className="mt-3 overflow-x-auto rounded-md bg-slate-900 p-3 text-xs text-slate-100">
{`{
  "external_id": "article-123",
  "title": "Membership Cancellation SOP",
  "body": "Document text...",
  "url": "https://example.com/article-123",
  "category": "Memberships",
  "tags": ["membership", "cancellation"],
  "source_updated_at": "2026-04-24T12:00:00Z"
}`}
            </pre>
            <p className="mt-3 text-xs text-slate-500">
              Use a stable `external_id` so repeated Zap runs update the same document instead of
              creating duplicates.
            </p>
          </div>
        </div>
      </div>

      <ConfirmDialog
        isOpen={confirmDelete !== null}
        onClose={() => setConfirmDelete(null)}
        onConfirm={() => {
          if (confirmDelete !== null) deleteMutation.mutate(confirmDelete);
        }}
        title="Delete this connection?"
        message="This deletes the connection AND all of its synced documents. This cannot be undone."
        confirmLabel="Delete"
        variant="danger"
      />
    </div>
  );
}
